import React, { useEffect, useState } from "react";
import { getAuditRunProviders, getTgeScanRunResults } from "../api/audit-log-api.ts";
import { useAuditLogsQuery } from "../hooks/use-audit-logs-query.ts";
import type { AuditLogEntry, AuditLogProviderResult, TgeScanRunResult } from "../types/index.ts";

const PAGE_SIZE = 50;

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return "running...";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "completed"
      ? "audit-log__badge--completed"
      : status === "failed"
        ? "audit-log__badge--failed"
        : status === "running"
          ? "audit-log__badge--running"
          : "audit-log__badge--partial";
  return <span className={`audit-log__badge ${cls}`}>{status}</span>;
}

const ACTION_LABELS: Record<string, string> = {
  full_audit: "Full Audit",
  single_provider_rescore: "Provider Rescore",
  pedigree_rescore: "Pedigree Rescore",
  founder_override: "Founder Override",
  reweight: "Reweight",
  post_tge_rescore: "TGE Rescore",
  tge_manual_confirm: "TGE Confirmed",
  token_auto_archive: "Token Auto-Archive",
  score_reverted: "Score Reverted",
  score_invalidated: "Score Invalidated",
  tge_proximity_scan: "TGE Proximity Scan",
  handle_changed: "Handle Renamed",
};

const TRIGGER_LABELS: Record<string, string> = {
  api_submit: "Submit",
  api_rescore: "API Rescore",
  api_ingest: "Ingest",
  discovery: "Discovery",
  manual: "Manual",
  auto_rescore: "Auto Rescore",
  post_tge_rescore: "Post-TGE",
  pedigree_rescore: "Pedigree Rescore",
  tge_proximity: "TGE Proximity",
  incident: "Incident Recovery",
  unknown: "Unknown",
};

function ActionBadge({
  actionType,
  providerSlug,
}: {
  actionType: string;
  providerSlug: string | null;
}) {
  const label = ACTION_LABELS[actionType] ?? actionType;
  const suffix = providerSlug && actionType !== "full_audit" ? ` (${providerSlug})` : "";
  const cls =
    actionType === "full_audit"
      ? "audit-log__action--full"
      : actionType === "founder_override"
        ? "audit-log__action--override"
        : "audit-log__action--rescore";
  return (
    <span className={`audit-log__action ${cls}`}>
      {label}
      {suffix}
    </span>
  );
}

function formatActor(
  uid: string | null,
  email: string | null,
  canSeeActors: boolean
): string | null {
  // Prefer the human email when available; otherwise show the uid in a
  // friendly form. system:* uids are headless processes — strip the prefix
  // and humanize. `*-api-key` uids are shared-key roles — also non-PII.
  // Email + raw Firebase uid are PII; gated by canSeeActors for non-admin
  // viewers (Audit Log is open to basic users but actors are redacted).
  if (email) return canSeeActors ? email : null;
  if (!uid) return null;
  if (uid.startsWith("system:")) {
    return uid.slice("system:".length).replace(/[-_]/g, " ");
  }
  if (uid.endsWith("-api-key")) {
    return uid.replace("-api-key", " key");
  }
  return canSeeActors ? uid : null;
}

function TriggerBadge({
  source,
  email,
  uid,
  canSeeActors,
}: {
  source: string | null;
  email: string | null;
  uid: string | null;
  canSeeActors: boolean;
}) {
  if (!source) return <span className="audit-log__trigger">—</span>;

  // Split compound sources like "discovery:review:rootdata" or "discovery:rootdata:raise_change"
  const parts = source.split(":");
  const mainLabel = TRIGGER_LABELS[parts[0] ?? ""] ?? parts[0];
  const compoundDetail = parts.length > 1 ? parts.slice(1).join(":") : null;
  const actorLabel = formatActor(uid, email, canSeeActors);

  // Show approver for review-triggered runs
  const isReview = parts[1] === "review";

  return (
    <div className="audit-log__trigger-wrap">
      <span className="audit-log__trigger-main">{mainLabel}</span>
      {compoundDetail && <span className="audit-log__trigger-detail">{compoundDetail}</span>}
      {actorLabel && !isReview && <span className="audit-log__trigger-detail">{actorLabel}</span>}
      {isReview && email && canSeeActors && (
        <span className="audit-log__trigger-detail">{email}</span>
      )}
    </div>
  );
}

function ScoreCell({ entry }: { entry: AuditLogEntry }) {
  if (entry.alphaScore == null) return <span className="muted">—</span>;
  return (
    <span className="mono" title={`Confidence: ${entry.confidence ?? "—"}`}>
      {entry.alphaScore.toFixed(1)}
    </span>
  );
}

function ProviderCell({ entry }: { entry: AuditLogEntry }) {
  const total = entry.providerSuccessCount + entry.providerFailCount;
  if (total === 0) return <span className="muted">—</span>;
  return (
    <span className="mono">
      <span className="audit-log__provider-ok">{entry.providerSuccessCount}</span>/
      {entry.providerFailCount > 0 ? (
        <span className="audit-log__provider-fail">{entry.providerFailCount}</span>
      ) : (
        <span>{entry.providerFailCount}</span>
      )}
    </span>
  );
}

// ── Inline handle-change detail ──

function HandleChangeDetail({
  entry,
  canSeeActors,
}: {
  entry: AuditLogEntry;
  canSeeActors: boolean;
}) {
  const change = entry.handleChange;
  if (!change) return null;
  // Redact actor for non-admin viewers — only show system labels (which
  // aren't PII) verbatim; human actor falls back to a generic "admin".
  const rawUid = entry.triggeredByUid;
  const actorLabel = canSeeActors
    ? (entry.triggeredByEmail ?? rawUid ?? "admin")
    : rawUid?.startsWith("system:")
      ? rawUid.slice("system:".length).replace(/[-_]/g, " ")
      : "admin";
  return (
    <div className="audit-log__detail">
      <div className="audit-log__detail-item">
        <span className="audit-log__detail-label">Old handle</span>
        <span className="mono">@{change.oldHandle}</span>
      </div>
      <div className="audit-log__detail-item">
        <span className="audit-log__detail-label">New handle</span>
        <span className="mono">@{change.newHandle}</span>
      </div>
      {change.reason && (
        <div className="audit-log__detail-item">
          <span className="audit-log__detail-label">Reason</span>
          <span>{change.reason}</span>
        </div>
      )}
      <div className="audit-log__detail-item">
        <span className="audit-log__detail-label">Renamed by</span>
        <span>{actorLabel}</span>
      </div>
    </div>
  );
}

// ── Inline token auto-archive detail ──
//
// Spells out WHY a project was auto-archived: which coin was matched,
// what each source voted, and whether the CoinGecko match was by direct
// twitter-handle verification or bridged via a RootData contract
// address (parent→subsidiary case, e.g. OpenMind → Fabric Foundation →
// ROBO). The bare `{confidence, sources[]}` older shape gave the
// operator nothing to interpret — this surfaces the provenance.

function TokenAutoArchiveDetail({ entry }: { entry: AuditLogEntry }) {
  const data = entry.tokenAutoArchive;
  if (!data) {
    return (
      <div className="audit-log__detail">
        <div className="audit-log__detail-item">
          <span className="audit-log__detail-label">Info</span>
          <span className="muted">Legacy auto-archive entry — no enriched details available.</span>
        </div>
      </div>
    );
  }

  // Normalize sources: v2 object shape vs v1 legacy string[] of slugs.
  const v2Sources: Record<
    string,
    {
      hasToken?: boolean;
      isLive?: boolean;
      confidence?: number;
      matchedName?: string | null;
      matchedSymbol?: string | null;
      matchedTwitter?: string | null;
      geckoId?: string | null;
    }
  > = data.sources && !Array.isArray(data.sources) ? data.sources : {};
  const legacySlugs: string[] = Array.isArray(data.sources) ? data.sources : [];
  const sourceEntries = Object.entries(v2Sources);

  const matchedLabel =
    data.matchedName && data.matchedSymbol
      ? `${data.matchedName} (${data.matchedSymbol.toUpperCase()})`
      : (data.matchedName ?? data.matchedSymbol ?? data.geckoId ?? "Unknown token");

  const methodLabel =
    data.cgDetection?.method === "contract_bridge"
      ? "Bridged via RootData contract → CoinGecko"
      : data.cgDetection?.method === "direct_handle_match"
        ? "Direct CoinGecko handle match"
        : null;

  const bridge = data.cgDetection?.bridgeContract;

  return (
    <div className="audit-log__detail">
      <div className="audit-log__detail-item">
        <span className="audit-log__detail-label">Matched token</span>
        <span className="mono">
          {matchedLabel}
          {data.geckoId && (
            <>
              {" "}
              —{" "}
              <a
                href={`https://www.coingecko.com/en/coins/${data.geckoId}`}
                target="_blank"
                rel="noreferrer"
                className="muted"
              >
                coingecko.com/{data.geckoId}
              </a>
            </>
          )}
        </span>
      </div>

      {data.confidence != null && (
        <div className="audit-log__detail-item">
          <span className="audit-log__detail-label">Confidence</span>
          <span className="mono">{data.confidence}%</span>
        </div>
      )}

      {methodLabel && (
        <div className="audit-log__detail-item">
          <span className="audit-log__detail-label">How found</span>
          <span>
            {methodLabel}
            {bridge && (
              <>
                {" "}
                <span className="mono muted">
                  ({bridge.platform} {bridge.address.slice(0, 10)}…{bridge.address.slice(-6)})
                </span>
              </>
            )}
          </span>
        </div>
      )}

      {sourceEntries.length > 0 ? (
        <div className="audit-log__detail-item audit-log__detail-item--full">
          <span className="audit-log__detail-label">Source evidence</span>
          <div className="audit-log__provider-grid">
            {sourceEntries.map(([slug, info]) => {
              const handleMismatch =
                info.matchedTwitter &&
                data.projectHandle &&
                info.matchedTwitter.toLowerCase() !== data.projectHandle.toLowerCase();
              return (
                <div key={slug} className="audit-log__provider-row">
                  <span className="audit-log__provider-name">{slug}</span>
                  <span className="mono">
                    {info.matchedName
                      ? `${info.matchedName}${info.matchedSymbol ? ` / ${info.matchedSymbol.toUpperCase()}` : ""}`
                      : (info.matchedSymbol ?? "—")}
                  </span>
                  {info.matchedTwitter && (
                    <span
                      className="mono"
                      style={{ color: handleMismatch ? "#f59e0b" : undefined }}
                      title={
                        handleMismatch
                          ? `Handle differs from project @${data.projectHandle} — legitimate for parent→subsidiary cases (Fabric Foundation ↔ OpenMind)`
                          : undefined
                      }
                    >
                      @{info.matchedTwitter}
                    </span>
                  )}
                  <span className="muted">
                    {info.isLive ? "live" : "not live"} · {info.confidence ?? "—"}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : legacySlugs.length > 0 ? (
        <div className="audit-log__detail-item">
          <span className="audit-log__detail-label">Sources</span>
          <span className="mono">{legacySlugs.join(", ")}</span>
        </div>
      ) : null}
    </div>
  );
}

// ── Inline TGE scan detail (rendered inside the expand row of the audit log table) ──

function TgeScanDetail({ entry }: { entry: AuditLogEntry }) {
  const scan = entry.tgeScan;
  const [results, setResults] = useState<TgeScanRunResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!scan) return;
    let cancelled = false;
    getTgeScanRunResults(scan.runId).then((data) => {
      if (!cancelled) {
        setResults(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [scan]);

  if (!scan) return null;

  return (
    <div className="audit-log__detail">
      <div className="audit-log__detail-item">
        <strong>Tier:</strong>{" "}
        {scan.tier === "high"
          ? "Weekly (high-confidence)"
          : scan.tier === "watch"
            ? "Biweekly (watch-tier 25–49%)"
            : "Monthly (full)"}
      </div>
      <div className="audit-log__detail-item">
        <strong>Scanned:</strong> {scan.scannedCount} projects
        {" · "}
        <strong>Signals:</strong> {scan.signalsDetected}
        {" · "}
        <strong>Updated:</strong> {scan.updatedCount}
        {scan.errors > 0 && (
          <>
            {" · "}
            <span style={{ color: "#ef4444" }}>
              <strong>Errors:</strong> {scan.errors}
            </span>
          </>
        )}
      </div>
      {entry.notes && (
        <div className="audit-log__detail-item" style={{ color: "#ef4444" }}>
          {entry.notes}
        </div>
      )}

      {loading ? (
        <div className="spinner" style={{ margin: "0.5rem auto" }} />
      ) : results.length > 0 ? (
        <div className="tge-scan-results" style={{ marginTop: "0.5rem" }}>
          {results.map((r) => (
            <div key={`${r.projectId}-${r.outcome}`} className="tge-scan-result-row">
              <span className="tge-scan-result-row__handle">@{r.twitterHandle}</span>
              <span
                className={`tge-scan-result-row__outcome tge-scan-result-row__outcome--${r.outcome}`}
              >
                {r.outcome}
              </span>
              <span className="tge-scan-result-row__confidence">
                {r.confidenceBefore ?? 0}%
                {r.outcome !== "unchanged" && (
                  <>
                    {" → "}
                    <strong>{r.confidenceAfter ?? 0}%</strong>
                  </>
                )}
              </span>
              {r.estimatedDateAfter && (
                <span className="tge-scan-result-row__date">~{r.estimatedDateAfter}</span>
              )}
            </div>
          ))}
        </div>
      ) : scan.updatedProjects?.length ? (
        <div className="audit-log__detail-item" style={{ marginTop: "0.5rem" }}>
          <strong>Updated handles:</strong> {scan.updatedProjects.join(", ")}
        </div>
      ) : (
        <div className="muted" style={{ marginTop: "0.5rem" }}>
          No per-project detail available (scan predates result tracking)
        </div>
      )}
    </div>
  );
}

function InlineTgeRescanDetail({ entry }: { entry: AuditLogEntry }) {
  const r = entry.inlineTgeRescan;
  if (!r) return null;
  return (
    <div className="audit-log__detail">
      <div className="audit-log__detail-item">
        <strong>Trigger:</strong> Token detection flipped during audit
      </div>
      {r.flippedSources.length > 0 && (
        <div className="audit-log__detail-item">
          <strong>Flipped sources:</strong> {r.flippedSources.join(", ")}
          {r.tokenConfidence != null && (
            <>
              {" · "}
              <strong>Token confidence:</strong> {r.tokenConfidence}%
            </>
          )}
        </div>
      )}
      <div className="audit-log__detail-item">
        <strong>TGE confidence:</strong> {r.confidenceBefore ?? 0}%{" → "}
        <strong>{r.confidenceAfter ?? 0}%</strong>
        {" · "}
        <strong>Sources:</strong> {r.sourcesCount}
      </div>
      {r.signalsAfter.length > 0 && (
        <div className="audit-log__detail-item">
          <strong>Signals:</strong> {r.signalsAfter.join(", ")}
        </div>
      )}
      {!r.ranScan && r.skippedReason && (
        <div className="audit-log__detail-item muted">
          <strong>Skipped:</strong> {r.skippedReason}
        </div>
      )}
    </div>
  );
}

export function AuditLogView({ canSeeActors = false }: { canSeeActors?: boolean } = {}) {
  const [page, setPage] = useState(0);
  const [actionType, setActionType] = useState("all");
  const [triggerSource, setTriggerSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [providerData, setProviderData] = useState<Record<string, AuditLogProviderResult[]>>({});
  const [loadingProviders, setLoadingProviders] = useState<string | null>(null);

  const { data, isLoading, error } = useAuditLogsQuery({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    actionType,
    triggerSource,
    status,
    search,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  useEffect(() => {
    if (!expandedId || providerData[expandedId]) return;
    const entry = data?.entries.find((e) => e.id === expandedId);
    if (!entry?.auditRunId || !entry.projectId) return;
    setLoadingProviders(expandedId);
    getAuditRunProviders(entry.projectId, entry.auditRunId).then((results) => {
      setProviderData((prev) => ({ ...prev, [expandedId]: results }));
      setLoadingProviders(null);
    });
  }, [expandedId, data?.entries, providerData]);

  const queueStatus = data?.queueStatus;

  return (
    <div className="audit-log">
      {queueStatus?.paused && (
        <div className="queue-paused-banner">
          <div className="queue-paused-banner__title">SCORING PAUSED</div>
          <div className="queue-paused-banner__reason">
            {queueStatus.globalTrip.tripped
              ? `Provider "${queueStatus.globalTrip.trippedBy}" failed ${Object.values(queueStatus.globalTrip.consecutiveFailures ?? {})[0] ?? "5+"} consecutive audits`
              : "Queues manually paused"}
          </div>
          {queueStatus.globalTrip.lastError && (
            <div className="queue-paused-banner__error">{queueStatus.globalTrip.lastError}</div>
          )}
          <div className="queue-paused-banner__queues">
            {Object.entries(queueStatus.queues)
              .filter(([, v]) => v)
              .map(([k]) => (
                <span key={k} className="queue-paused-banner__queue-chip">
                  {k}
                </span>
              ))}
          </div>
        </div>
      )}
      <div className="audit-log__header">
        <h2 className="audit-log__title">Audit Log</h2>
        <span className="audit-log__count">{data ? `${data.total} runs` : ""}</span>
      </div>

      <div className="audit-log__filters">
        <input
          type="text"
          className="audit-log__search"
          placeholder="Search project..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setSearch(searchInput);
              setPage(0);
            }
          }}
          onBlur={() => {
            if (searchInput !== search) {
              setSearch(searchInput);
              setPage(0);
            }
          }}
        />
        <select
          className="audit-log__filter-select"
          value={actionType}
          onChange={(e) => {
            setActionType(e.target.value);
            setPage(0);
          }}
        >
          <option value="all">All Actions</option>
          <option value="full_audit">Full Audit</option>
          <option value="single_provider_rescore">Provider Rescore</option>
          <option value="pedigree_rescore">Pedigree Rescore</option>
          <option value="founder_override">Founder Override</option>
          <option value="reweight">Reweight</option>
          <option value="post_tge_rescore">TGE Rescore</option>
          <option value="tge_proximity_scan">TGE Proximity Scan</option>
          <option value="tge_manual_confirm">TGE Confirmed</option>
          <option value="token_auto_archive">Token Auto-Archive</option>
          <option value="score_reverted">Score Reverted</option>
          <option value="score_invalidated">Score Invalidated</option>
          <option value="handle_changed">Handle Renamed</option>
        </select>
        <select
          className="audit-log__filter-select"
          value={triggerSource}
          onChange={(e) => {
            setTriggerSource(e.target.value);
            setPage(0);
          }}
        >
          <option value="all">All Triggers</option>
          <option value="api_submit">Submit</option>
          <option value="api_rescore">API Rescore</option>
          <option value="api_ingest">Ingest</option>
          <option value="discovery">Discovery</option>
          <option value="auto_rescore">Auto Rescore</option>
          <option value="post_tge_rescore">Post-TGE Rescore</option>
          <option value="pedigree_rescore">Pedigree Rescore</option>
          <option value="tge_proximity">TGE Proximity</option>
          <option value="manual">Manual</option>
          <option value="incident">Incident Recovery</option>
        </select>
        <select
          className="audit-log__filter-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
        >
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="partial">Partial</option>
          <option value="running">Running</option>
        </select>
      </div>

      {isLoading && <div className="audit-log__loading">Loading audit logs...</div>}
      {error && (
        <div className="audit-log__error">
          Failed to load audit logs: {(error as Error).message}
        </div>
      )}

      {data && data.entries.length === 0 && (
        <div className="audit-log__empty">No audit runs match the current filters.</div>
      )}

      {data && data.entries.length > 0 && (
        <>
          <div className="audit-log__table-wrap">
            <table className="audit-log__table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Providers</th>
                  <th>Trigger</th>
                  <th>Time</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => {
                  const isExpanded = expandedId === entry.id;
                  const isInlineTgeRescan = entry.inlineTgeRescan != null;
                  const isTgeScan = entry.actionType === "tge_proximity_scan" && !isInlineTgeRescan;
                  const isHandleChange = entry.actionType === "handle_changed";
                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        className={`audit-log__row audit-log__row--${entry.status}${isExpanded ? " audit-log__row--expanded" : ""}`}
                        onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      >
                        <td>
                          {isTgeScan ? (
                            <div className="audit-log__project">
                              <span
                                className="audit-log__project-name"
                                style={{ color: "#a855f7" }}
                              >
                                TGE Proximity Scan
                              </span>
                              <span className="audit-log__project-handle">
                                {entry.tgeScan?.scannedCount ?? 0} projects ·{" "}
                                {entry.tgeScan?.signalsDetected ?? 0} signals ·{" "}
                                {entry.tgeScan?.updatedCount ?? 0} updated
                              </span>
                            </div>
                          ) : isInlineTgeRescan ? (
                            <div className="audit-log__project">
                              <span className="audit-log__project-name">
                                {entry.projectName ?? entry.twitterHandle}
                              </span>
                              <span
                                className="audit-log__project-handle"
                                style={{ color: "#a855f7" }}
                              >
                                @{entry.twitterHandle} · audit-triggered rescan
                              </span>
                            </div>
                          ) : (
                            <div className="audit-log__project">
                              <span className="audit-log__project-name">
                                {entry.projectName ?? entry.twitterHandle}
                              </span>
                              <span className="audit-log__project-handle">
                                @{entry.twitterHandle}
                              </span>
                            </div>
                          )}
                        </td>
                        <td>
                          <ActionBadge
                            actionType={entry.actionType}
                            providerSlug={entry.providerSlug}
                          />
                        </td>
                        <td>
                          <StatusBadge status={entry.status} />
                        </td>
                        <td>
                          <ScoreCell entry={entry} />
                        </td>
                        <td>
                          <ProviderCell entry={entry} />
                        </td>
                        <td>
                          <TriggerBadge
                            source={entry.triggerSource}
                            email={entry.triggeredByEmail}
                            uid={entry.triggeredByUid}
                            canSeeActors={canSeeActors}
                          />
                        </td>
                        <td className="mono audit-log__date">{formatDate(entry.startedAt)}</td>
                        <td className="mono audit-log__duration">
                          {isHandleChange ? "—" : formatDuration(entry.startedAt, entry.finishedAt)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="audit-log__detail-row">
                          <td colSpan={8}>
                            {isTgeScan ? (
                              <TgeScanDetail entry={entry} />
                            ) : isInlineTgeRescan ? (
                              <InlineTgeRescanDetail entry={entry} />
                            ) : isHandleChange ? (
                              <HandleChangeDetail entry={entry} canSeeActors={canSeeActors} />
                            ) : entry.actionType === "token_auto_archive" ? (
                              <TokenAutoArchiveDetail entry={entry} />
                            ) : (
                              <div className="audit-log__detail">
                                {entry.scoreBefore != null && (
                                  <div className="audit-log__detail-item">
                                    <span className="audit-log__detail-label">Score Change</span>
                                    <span className="mono">
                                      {entry.scoreBefore.toFixed(1)} →{" "}
                                      {(entry.scoreAfter ?? 0).toFixed(1)}{" "}
                                      <span
                                        className={
                                          entry.scoreAfter != null &&
                                          entry.scoreAfter > entry.scoreBefore
                                            ? "audit-log__delta--up"
                                            : entry.scoreAfter != null &&
                                                entry.scoreAfter < entry.scoreBefore
                                              ? "audit-log__delta--down"
                                              : ""
                                        }
                                      >
                                        (
                                        {entry.scoreAfter != null
                                          ? (entry.scoreAfter - entry.scoreBefore > 0 ? "+" : "") +
                                            (entry.scoreAfter - entry.scoreBefore).toFixed(1)
                                          : "—"}
                                        )
                                      </span>
                                    </span>
                                  </div>
                                )}
                                {(entry.socialPoints != null || entry.backersPoints != null) && (
                                  <div className="audit-log__detail-item">
                                    <span className="audit-log__detail-label">Breakdown</span>
                                    <div className="audit-log__detail-breakdown">
                                      {[
                                        { label: "Social", value: entry.socialPoints, max: 20 },
                                        { label: "Backers", value: entry.backersPoints, max: 25 },
                                        { label: "Pedigree", value: entry.pedigreePoints, max: 20 },
                                        { label: "Momentum", value: entry.momentumPoints, max: 25 },
                                        { label: "Dev", value: entry.devPoints, max: 5 },
                                        { label: "Revenue", value: entry.revenuePoints, max: 5 },
                                      ].map((cat) => (
                                        <span
                                          key={cat.label}
                                          className="audit-log__detail-category"
                                        >
                                          <span className="audit-log__detail-cat-label">
                                            {cat.label}
                                          </span>
                                          <span className="mono">
                                            {cat.value != null ? cat.value.toFixed(1) : "—"}
                                          </span>
                                          <span className="audit-log__detail-cat-max">
                                            /{cat.max}
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {entry.providerSuccessCount + entry.providerFailCount > 0 && (
                                  <div className="audit-log__detail-item">
                                    <span className="audit-log__detail-label">Providers</span>
                                    <span className="mono">
                                      {entry.providerSuccessCount} succeeded,{" "}
                                      {entry.providerFailCount} failed
                                    </span>
                                  </div>
                                )}
                                {entry.auditRunId && (
                                  <div className="audit-log__detail-item audit-log__detail-item--full">
                                    <span className="audit-log__detail-label">Providers</span>
                                    {loadingProviders === entry.id ? (
                                      <span className="muted">Loading...</span>
                                    ) : providerData[entry.id]?.length ? (
                                      <div className="audit-log__provider-grid">
                                        {(providerData[entry.id] ?? []).map((p) => (
                                          <div
                                            key={p.providerSlug}
                                            className={`audit-log__provider-row${p.success ? "" : " audit-log__provider-row--failed"}`}
                                          >
                                            <span className="audit-log__provider-name">
                                              {p.providerSlug}
                                            </span>
                                            {p.success ? (
                                              <span className="audit-log__provider-score mono">
                                                {p.subScore != null ? p.subScore.toFixed(3) : "—"}
                                              </span>
                                            ) : (
                                              <span className="audit-log__provider-error">
                                                {p.errorMessage || "Failed"}
                                              </span>
                                            )}
                                            {p.durationMs != null && (
                                              <span className="audit-log__provider-duration mono">
                                                {p.durationMs < 1000
                                                  ? `${p.durationMs}ms`
                                                  : `${(p.durationMs / 1000).toFixed(1)}s`}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="muted">No evidence available</span>
                                    )}
                                  </div>
                                )}
                                {entry.notes && (
                                  <div className="audit-log__detail-item">
                                    <span className="audit-log__detail-label">Notes</span>
                                    <span>{entry.notes}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="audit-log__pagination">
              <button
                type="button"
                className="btn btn-small btn-secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="audit-log__page-info">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-small btn-secondary"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
